import {
  CACHE_DIM_HARD_MINIMUMS,
  computeCacheDimensionsFromInstall,
} from "./evalCacheDimFormula";

/** Intention utilisateur — seuls paramètres modèle à inclure dans un lien de partage. */
export const SHARE_MODEL_KEYS = [
  "pattern",
  "type",
  "wallmount",
  "bottom",
  "rear",
  "sizeL",
  "sizeH",
  "sizeP1",
  "sizeP2",
];

/** Dimensions cache explicites (uniquement si l'utilisateur les a modifiées à la main). */
export const SHARE_CACHE_KEYS = ["sizeX", "sizeY", "sizeZ", "frontHeight"];

export const SHARE_MATERIAL_KEYS = ["material"];

/** Booléens modèle — doivent être parsés explicitement (sinon "false" reste truthy). */
const SHARE_BOOL_MODEL_KEYS = new Set(["wallmount", "bottom", "rear"]);

const parseUrlBool = (value) => value === "true" || value === "1";

const parseShareModelValue = (key, value) => {
  if (SHARE_BOOL_MODEL_KEYS.has(key)) {
    return parseUrlBool(value);
  }
  if (key === "pattern") {
    return String(value);
  }
  if (key === "type") {
    return String(value);
  }
  const asNumber = Number(value);
  return Number.isNaN(asNumber) ? value : asNumber;
};

/**
 * État interne / dérivé — ne doit jamais être sérialisé ni restauré depuis l'URL.
 * Les anciens liens qui les contenaient figeaient des dimensions cache hors formules
 * (ex. sizeZ=620 au lieu de 600) et faussaient le prix affiché.
 */
export const isLegacyBrokenShareUrl = (search) => {
  const params = new URLSearchParams(search);
  return parseUrlBool(params.get("aModifierDimensionsCache") ?? "");
};

export const urlHasInstallDimensions = (search = "") => {
  const params = new URLSearchParams(search);
  return ["sizeL", "sizeH", "sizeP1", "sizeP2"].some((key) => params.has(key));
};

export const urlHasCustomCacheDimensions = (search = "") => {
  const params = new URLSearchParams(search);
  return (
    parseUrlBool(params.get("cacheCustom") ?? "")
    && !isLegacyBrokenShareUrl(search)
  );
};

export const parseShareParamsFromSearch = (search) => {
  const params = new URLSearchParams(search);
  const modelData = {};
  const materialData = {};

  const cacheCustom = urlHasCustomCacheDimensions(search);

  for (const key of SHARE_MODEL_KEYS) {
    const value = params.get(key);
    if (value === null || value === "") continue;
    modelData[key] = parseShareModelValue(key, value);
  }

  if (cacheCustom) {
    for (const key of SHARE_CACHE_KEYS) {
      const value = params.get(key);
      if (value === null || value === "") continue;
      modelData[key] = Number(value);
    }
  }

  for (const key of SHARE_MATERIAL_KEYS) {
    const value = params.get(key);
    if (value === null || value === "") continue;
    materialData[key] = String(value);
  }

  return { modelData, materialData, cacheCustom };
};

/** Applique les paramètres de partage sur l'état modèle initial (1er rendu). */
export const applyShareParamsToModelDefaults = (defaults, search = "") => {
  const { modelData, cacheCustom } = parseShareParamsFromSearch(search);
  if (Object.keys(modelData).length === 0 && !cacheCustom) {
    return defaults;
  }

  const merged = {
    ...defaults,
    ...modelData,
    ...(cacheCustom
      ? {
          aModifierDimensionsCache: true,
          cacheManualWidth: true,
          cacheManualDepth: true,
          cacheManualRearY: true,
        }
      : {}),
  };

  const shouldPrecomputeCache =
    !cacheCustom
    && urlHasInstallDimensions(search)
    && merged.sizeL > 0
    && merged.sizeH > 0
    && (merged.sizeP1 > 0 || merged.sizeP2 > 0);

  if (!shouldPrecomputeCache) {
    return merged;
  }

  const cacheDims = computeCacheDimensionsFromInstall({
    sizeL: merged.sizeL,
    sizeH: merged.sizeH,
    sizeP1: merged.sizeP1,
    sizeP2: merged.sizeP2,
    type: merged.type,
    wallmount: merged.wallmount,
    bottom: merged.bottom,
    rear: merged.rear,
    cacheMinimums: CACHE_DIM_HARD_MINIMUMS,
  });

  return {
    ...merged,
    ...cacheDims,
  };
};

/** Lien de partage standard (cotes install) : le prix doit attendre le productId. */
export const shouldDeferPriceForShareUrl = (search = "") =>
  urlHasInstallDimensions(search) && !urlHasCustomCacheDimensions(search);

export const applyShareParamsToMaterialDefaults = (defaults, search = "") => {
  const { materialData } = parseShareParamsFromSearch(search);
  if (Object.keys(materialData).length === 0) {
    return defaults;
  }
  return { ...defaults, ...materialData };
};

export const buildShareSearchParams = (modelFormData, materialFormData) => {
  const params = new URLSearchParams();

  for (const key of SHARE_MODEL_KEYS) {
    const value = modelFormData[key];
    if (value === null || value === undefined || value === "") continue;
    params.append(key, String(value));
  }

  if (modelFormData.aModifierDimensionsCache) {
    params.set("cacheCustom", "1");
    for (const key of SHARE_CACHE_KEYS) {
      const value = modelFormData[key];
      if (value === null || value === undefined || value === "") continue;
      params.append(key, String(value));
    }
  }

  for (const key of SHARE_MATERIAL_KEYS) {
    const value = materialFormData[key];
    if (value === null || value === undefined || value === "") continue;
    params.append(key, String(value));
  }

  return params;
};
